import { useCircle, useGoogleMaps, useMap, useMarkers, } from '@hooks/useGoogleMaps';
import debounce from 'lodash/debounce';
import * as React from 'react';
import S from './GoogleMap.styles';
const GoogleMap = ({ apiKey, center, clustering = false, distance, icon, mapOptions, markers = [], onMarkerClick, zoomToBounds, }) => {
    const googleApi = useGoogleMaps({ apiOptions: { apiKey } });
    const [refMapElement, map] = useMap({
        api: googleApi,
        mapOptions: mapOptions,
    });
    const [, bounds] = useMarkers({
        api: googleApi,
        clustering: clustering,
        icon: icon,
        map: map,
        markers: markers,
        onMarkerClick: onMarkerClick,
    });
    const [circle] = useCircle({
        api: googleApi,
        center: center,
        radius: distance ? distance * 1609 : undefined,
        map: map,
        clear: !center,
    });
    // Create debounced resize handler.
    const handleResizeDebounced = debounce(handleResize, 100);
    // Handle updating map position on load and resize.
    React.useEffect(() => {
        if (!map) {
            return;
        }
        handleResize();
        window.addEventListener('resize', handleResizeDebounced, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResizeDebounced);
        };
    }, [map, bounds, center?.lat, center?.lng, circle, zoomToBounds]);
    return React.createElement(S.Container, { ref: refMapElement });
    function handleResize() {
        // Move to global marker bounds.
        if (map && bounds && zoomToBounds) {
            map.fitBounds(bounds);
            return;
        }
        // Move map to circle and zoom to bounds.
        if (map && circle && center) {
            const circleBounds = circle.getBounds();
            if (circleBounds) {
                map.fitBounds(circleBounds);
                return;
            }
        }
        // Move map to center point and zoom.
        if (map && center) {
            map.setCenter(center);
            return;
        }
        // Move to marker bounds if not too small.
        if (map && bounds && !isBoundsSmall(bounds)) {
            map.fitBounds(bounds);
            return;
        }
    }
};
export default GoogleMap;
function isBoundsSmall(bounds) {
    if (bounds.isEmpty()) {
        return true;
    }
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    if (ne.lat() - sw.lat() < 10) {
        return true;
    }
    if (ne.lng() - sw.lng() < 10) {
        return true;
    }
    return false;
}
